import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LanguagePicker from '@/components/settings/LanguagePicker';
import { ArrowLeft, Camera, Monitor, Smartphone, LogOut, Trash2, Shield, KeyRound, Copy, Check, Fingerprint, Plus, Pencil, Bell, BellOff, EyeOff, Download, AlertTriangle } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { usersApi } from '@/api/users';
import { authApi, PRIVACY_AUDIENCE_LABELS, PrivacyAudience, PrivacySettings } from '@/api/auth';
import { notificationsApi, type NotificationTypeKey } from '@/api/notifications';
import { totpApi } from '@/api/totp';
import { webauthnApi, type PasskeyCredential } from '@/api/webauthn';
import { initPushNotifications, unsubscribePush } from '@/lib/pushSubscription';
import Avatar from '@/components/ui/Avatar';
import RelayPanel from '@/components/chat/RelayPanel';
import { useUiPrefsStore, MENTION_COLOR_PRESETS } from '@/store/uiPrefsStore';
import { useBlockedUsersStore } from '@/store/blockedUsersStore';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

function PasskeysSection() {
  const qc = useQueryClient();
  const supported = typeof window !== 'undefined' && webauthnApi.isSupported();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const { data: creds, isLoading } = useQuery({
    queryKey: ['passkeys'],
    queryFn: () => webauthnApi.list(),
    enabled: supported,
  });

  const handleRegister = async () => {
    setError(null);
    setBusy(true);
    try {
      await webauthnApi.register(label.trim() || undefined);
      setLabel('');
      qc.invalidateQueries({ queryKey: ['passkeys'] });
    } catch (e) {
      const msg =
        (e as { response?: { data?: { message?: string } }; message?: string })?.response?.data
          ?.message ?? (e as Error)?.message ?? 'Не удалось добавить ключ';
      // Friendlier message for the common cancel case.
      if (/NotAllowed|cancel|aborted/i.test(msg)) {
        setError('Операция отменена пользователем');
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (id: string) => {
    if (!confirm('Удалить этот ключ доступа? Войти с ним больше не получится.')) return;
    setError(null);
    try {
      await webauthnApi.remove(id);
      qc.invalidateQueries({ queryKey: ['passkeys'] });
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Не удалось удалить');
    }
  };

  const handleRename = async (id: string) => {
    const v = renameValue.trim();
    if (!v) { setRenamingId(null); return; }
    try {
      await webauthnApi.rename(id, v);
      qc.invalidateQueries({ queryKey: ['passkeys'] });
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Не удалось переименовать');
    } finally {
      setRenamingId(null);
      setRenameValue('');
    }
  };

  return (
    <section className="bg-surface-900 border border-surface-800 rounded-xl p-5">
      <h3 className="text-surface-100 font-semibold mb-3 flex items-center gap-2">
        <Fingerprint size={16} className="text-brand-400" />
        Ключи доступа (Passkeys)
      </h3>
      <p className="text-surface-500 text-xs mb-4">
        Вход без пароля по биометрии или PIN устройства. Один аккаунт может иметь несколько ключей —
        для телефона, ноутбука, аппаратного токена.
      </p>

      {!supported && (
        <div className="bg-amber-900/30 border border-amber-800/50 text-amber-300 text-xs rounded-lg px-3 py-2">
          Этот браузер не поддерживает WebAuthn / passkeys. Используйте современный браузер
          (Chrome, Edge, Safari, Firefox последних версий).
        </div>
      )}

      {error && (
        <div className="bg-red-900/30 border border-red-800/50 text-red-300 text-xs rounded-lg px-3 py-2 mb-3">{error}</div>
      )}

      {supported && (
        <>
          <div className="space-y-2 mb-4">
            <label className="block text-surface-300 text-xs font-medium">Название устройства (необязательно)</label>
            <div className="flex gap-2">
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Например: iPhone, MacBook, YubiKey"
                maxLength={64}
                className="irc-input flex-1"
              />
              <button onClick={handleRegister} disabled={busy} className="btn-primary flex items-center gap-1.5">
                <Plus size={14} />
                {busy ? '...' : 'Добавить'}
              </button>
            </div>
          </div>

          {isLoading && <p className="text-surface-500 text-sm">Загрузка...</p>}

          {creds && creds.length === 0 && !isLoading && (
            <p className="text-surface-500 text-xs">Нет зарегистрированных ключей.</p>
          )}

          <div className="space-y-2">
            {creds?.map((c: PasskeyCredential) => (
              <div
                key={c.id}
                className="flex items-center gap-3 p-3 rounded-xl border bg-surface-800 border-surface-700"
              >
                <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 bg-surface-700">
                  <Fingerprint size={16} className="text-surface-300" />
                </div>
                <div className="flex-1 min-w-0">
                  {renamingId === c.id ? (
                    <div className="flex gap-2">
                      <input
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        autoFocus
                        maxLength={64}
                        className="irc-input flex-1 text-xs"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRename(c.id);
                          if (e.key === 'Escape') { setRenamingId(null); setRenameValue(''); }
                        }}
                      />
                      <button onClick={() => handleRename(c.id)} className="btn-primary text-xs">OK</button>
                    </div>
                  ) : (
                    <>
                      <p className="text-surface-100 text-sm font-medium truncate">
                        {c.deviceLabel || 'Без имени'}
                      </p>
                      <p className="text-surface-500 text-xs">
                        Добавлен {format(new Date(c.createdAt), 'd MMM yyyy', { locale: ru })}
                        {c.lastUsedAt && ` · использован ${format(new Date(c.lastUsedAt), 'd MMM yyyy', { locale: ru })}`}
                        {c.backupEligible && ' · синхронизируется'}
                      </p>
                    </>
                  )}
                </div>
                {renamingId !== c.id && (
                  <>
                    <button
                      onClick={() => { setRenamingId(c.id); setRenameValue(c.deviceLabel ?? ''); }}
                      title="Переименовать"
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-surface-500 hover:text-brand-400 hover:bg-surface-700 transition-colors flex-shrink-0"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      onClick={() => handleRemove(c.id)}
                      title="Удалить"
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-surface-500 hover:text-red-400 hover:bg-surface-700 transition-colors flex-shrink-0"
                    >
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function TotpSection() {
  const { user, setUser } = useAuthStore();
  const enabled = user?.totpEnabled ?? false;
  const [enrollment, setEnrollment] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [disableMode, setDisableMode] = useState(false);

  const begin = async () => {
    setError(null);
    try {
      const data = await totpApi.enroll();
      setEnrollment(data);
      setCode('');
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Не удалось начать подключение');
    }
  };

  const confirm = async () => {
    if (!/^\d{6}$/.test(code)) { setError('Введите 6-значный код'); return; }
    setError(null);
    try {
      await totpApi.confirm(code);
      if (user) setUser({ ...user, totpEnabled: true });
      setEnrollment(null);
      setCode('');
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Неверный код');
    }
  };

  const disable = async () => {
    if (!/^\d{6}$/.test(code)) { setError('Введите 6-значный код'); return; }
    setError(null);
    try {
      await totpApi.disable(code);
      if (user) setUser({ ...user, totpEnabled: false });
      setDisableMode(false);
      setCode('');
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Неверный код');
    }
  };

  const copySecret = () => {
    if (!enrollment) return;
    navigator.clipboard.writeText(enrollment.secret).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <section className="bg-surface-900 border border-surface-800 rounded-xl p-5">
      <h3 className="text-surface-100 font-semibold mb-3 flex items-center gap-2">
        <KeyRound size={16} className="text-brand-400" />
        Двухфакторная аутентификация
      </h3>
      <p className="text-surface-500 text-xs mb-4">
        Дополнительная защита аккаунта одноразовым кодом из приложения (Google Authenticator, Authy, 1Password и т.п.).
      </p>

      {error && (
        <div className="bg-red-900/30 border border-red-800/50 text-red-300 text-xs rounded-lg px-3 py-2 mb-3">{error}</div>
      )}

      {!enabled && !enrollment && (
        <button onClick={begin} className="btn-primary">Включить 2FA</button>
      )}

      {!enabled && enrollment && (
        <div className="space-y-3">
          <p className="text-surface-300 text-xs">
            Откройте приложение-аутентификатор и отсканируйте QR или введите ключ вручную:
          </p>
          <div className="flex items-center gap-2 bg-surface-800 border border-surface-700 rounded-lg px-3 py-2">
            <code className="text-xs font-mono text-surface-200 break-all flex-1">{enrollment.secret}</code>
            <button onClick={copySecret} className="text-surface-400 hover:text-brand-400 flex-shrink-0">
              {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
            </button>
          </div>
          <div className="flex justify-center">
            <img
              alt="QR код 2FA"
              className="rounded-lg bg-white p-2"
              src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(enrollment.otpauthUrl)}`}
            />
          </div>
          <div>
            <label className="block text-surface-300 text-xs font-medium mb-1.5">
              Введите код из приложения
            </label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              className="irc-input font-mono tracking-widest text-center"
              maxLength={6}
            />
          </div>
          <div className="flex gap-2">
            <button onClick={confirm} className="btn-primary flex-1">Подтвердить</button>
            <button onClick={() => { setEnrollment(null); setCode(''); setError(null); }} className="btn-ghost">
              Отмена
            </button>
          </div>
        </div>
      )}

      {enabled && !disableMode && (
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1 text-green-400 text-xs px-2 py-1 rounded bg-green-900/30 border border-green-800/40">
            <Check size={12} /> Включена
          </span>
          <button onClick={() => { setDisableMode(true); setCode(''); setError(null); }}
            className="text-xs text-red-400 hover:text-red-300">
            Отключить
          </button>
        </div>
      )}

      {enabled && disableMode && (
        <div className="space-y-2">
          <p className="text-surface-300 text-xs">
            Введите текущий код из приложения для отключения 2FA:
          </p>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            className="irc-input font-mono tracking-widest text-center"
            maxLength={6}
          />
          <div className="flex gap-2">
            <button onClick={disable} className="flex-1 py-2 px-4 rounded-lg bg-red-700 hover:bg-red-600 text-white text-sm font-medium">
              Отключить
            </button>
            <button onClick={() => { setDisableMode(false); setCode(''); setError(null); }} className="btn-ghost">
              Отмена
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function parseUA(ua: string | null): string {
  if (!ua) return 'Неизвестное устройство';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iPhone / iOS';
  if (/Android/i.test(ua)) return 'Android';
  if (/Windows/i.test(ua)) {
    if (/Chrome/i.test(ua)) return 'Chrome / Windows';
    if (/Firefox/i.test(ua)) return 'Firefox / Windows';
    if (/Edge/i.test(ua)) return 'Edge / Windows';
    return 'Windows';
  }
  if (/Mac/i.test(ua)) {
    if (/Chrome/i.test(ua)) return 'Chrome / macOS';
    if (/Firefox/i.test(ua)) return 'Firefox / macOS';
    if (/Safari/i.test(ua)) return 'Safari / macOS';
    return 'macOS';
  }
  if (/Linux/i.test(ua)) return 'Linux';
  return 'Браузер';
}

function isMobileUA(ua: string | null): boolean {
  if (!ua) return false;
  return /iPhone|iPad|iPod|Android|Mobile/i.test(ua);
}

function NotificationsSection() {
  const supported = typeof window !== 'undefined'
    && 'Notification' in window
    && 'serviceWorker' in navigator
    && 'PushManager' in window;
  const [permission, setPermission] = useState<NotificationPermission>(
    supported ? Notification.permission : 'denied',
  );
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  // Check current subscription on mount.
  useEffect(() => {
    if (!supported) { setSubscribed(false); return; }
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setSubscribed(!!sub))
      .catch(() => setSubscribed(false));
  }, [supported]);

  const handleEnable = async () => {
    setBusy(true);
    try {
      await initPushNotifications();
      setPermission(Notification.permission);
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    try {
      await unsubscribePush();
      setSubscribed(false);
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <section className="bg-surface-900 border border-surface-800 rounded-xl p-5">
        <h3 className="text-surface-100 font-semibold flex items-center gap-2 mb-2">
          <BellOff size={16} className="text-surface-400" />
          Уведомления
        </h3>
        <p className="text-surface-500 text-sm">Браузер не поддерживает push-уведомления.</p>
      </section>
    );
  }

  return (
    <section className="bg-surface-900 border border-surface-800 rounded-xl p-5">
      <h3 className="text-surface-100 font-semibold flex items-center gap-2 mb-4">
        <Bell size={16} className="text-brand-400" />
        Уведомления
      </h3>

      {permission === 'denied' ? (
        <div>
          <p className="text-surface-400 text-sm mb-2">
            Разрешение на уведомления заблокировано в настройках браузера.
          </p>
          <p className="text-surface-500 text-xs">
            Откройте замочек в адресной строке → разрешите «Уведомления», затем перезагрузите страницу.
          </p>
        </div>
      ) : subscribed ? (
        <div className="flex items-center justify-between">
          <div>
            <p className="text-surface-200 text-sm">Push-уведомления включены</p>
            <p className="text-surface-500 text-xs mt-0.5">
              Вам будут приходить уведомления о сообщениях и звонках даже с закрытой вкладкой.
            </p>
          </div>
          <button
            onClick={handleDisable}
            disabled={busy}
            className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1.5 transition-colors disabled:opacity-50"
          >
            <BellOff size={14} />
            Выключить
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div>
            <p className="text-surface-300 text-sm">Уведомления выключены</p>
            <p className="text-surface-500 text-xs mt-0.5">
              Получайте сообщения и звонки, даже когда вкладка закрыта.
            </p>
          </div>
          <button
            onClick={handleEnable}
            disabled={busy}
            className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5"
          >
            <Bell size={14} />
            {busy ? 'Подключение...' : 'Включить'}
          </button>
        </div>
      )}
    </section>
  );
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuthStore();
  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [bio, setBio] = useState(user?.bio ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const qc = useQueryClient();

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const updated = await usersApi.updateProfile({ displayName, bio });
      setUser({ ...user!, ...updated });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { avatarUrl } = await usersApi.uploadAvatar(file);
      setUser({ ...user!, avatarUrl });
    } catch { /* ignore */ }
  };

  const { data: sessions, isLoading: sessionsLoading } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => authApi.getSessions(),
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) => authApi.revokeSession(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessions'] }),
  });

  const revokeAllMut = useMutation({
    mutationFn: () => authApi.revokeAllSessions(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessions'] }),
  });

  const handleLogoutEverywhere = async () => {
    if (!confirm('Завершить все сессии кроме текущей?')) return;
    await revokeAllMut.mutateAsync();
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-surface-950">
      {/* Header */}
      <div className="h-12 border-b border-surface-800 px-4 flex items-center gap-3 flex-shrink-0">
        <button onClick={() => navigate('/')} className="btn-ghost p-1.5 text-surface-400">
          <ArrowLeft size={18} />
        </button>
        <span className="font-semibold text-surface-200">Настройки</span>
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="max-w-lg mx-auto space-y-6">

          <MentionColorPicker />

          <BlockedUsersList />

          {/* Profile */}
          <section className="bg-surface-900 border border-surface-800 rounded-xl p-5">
            <h3 className="text-surface-100 font-semibold mb-4">Профиль</h3>

            <div className="flex items-center gap-4 mb-4">
              <div className="relative">
                <Avatar src={user?.avatarUrl} username={user?.username ?? '?'} size="lg" />
                <label className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-brand-600 flex items-center justify-center cursor-pointer hover:bg-brand-500 transition-colors">
                  <Camera size={13} className="text-white" />
                  <input type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
                </label>
              </div>
              <div>
                <p className="text-surface-100 font-medium">{user?.displayName}</p>
                <p className="text-surface-500 text-sm font-mono">@{user?.username}</p>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-surface-300 text-xs font-medium mb-1.5">Отображаемое имя</label>
                <input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="irc-input"
                  maxLength={64}
                />
              </div>
              <div>
                <label className="block text-surface-300 text-xs font-medium mb-1.5">О себе</label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  className="irc-input resize-none"
                  rows={3}
                  maxLength={512}
                  placeholder="Расскажите о себе..."
                />
              </div>
              <button onClick={handleSaveProfile} disabled={saving} className="btn-primary">
                {saving ? 'Сохранение...' : saved ? 'Сохранено!' : 'Сохранить'}
              </button>
            </div>
          </section>

          {/* Notifications */}
          <NotificationsSection />

          {/* Two-factor auth */}
          <TotpSection />

          {/* Passkeys */}
          <PasskeysSection />

          {(user?.karma ?? 0) >= 0xF4240 && <RelayPanel />}

          {/* Active sessions */}
          <section className="bg-surface-900 border border-surface-800 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-surface-100 font-semibold flex items-center gap-2">
                <Shield size={16} className="text-brand-400" />
                Активные сессии
              </h3>
              {sessions && sessions.length > 1 && (
                <button
                  onClick={handleLogoutEverywhere}
                  disabled={revokeAllMut.isPending}
                  className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1 transition-colors"
                >
                  <Trash2 size={12} />
                  Завершить все остальные
                </button>
              )}
            </div>

            {sessionsLoading && (
              <p className="text-surface-500 text-sm">Загрузка...</p>
            )}

            <div className="space-y-2">
              {sessions?.map((session) => (
                <div
                  key={session.id}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                    session.isCurrent
                      ? 'bg-brand-900/30 border-brand-700/50'
                      : 'bg-surface-800 border-surface-700'
                  }`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                    session.isCurrent ? 'bg-brand-600' : 'bg-surface-700'
                  }`}>
                    {isMobileUA(session.userAgent)
                      ? <Smartphone size={16} className="text-surface-300" />
                      : <Monitor size={16} className="text-surface-300" />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-surface-100 text-sm font-medium truncate">
                        {parseUA(session.userAgent)}
                      </p>
                      {session.isCurrent && (
                        <span className="text-[10px] bg-brand-600 text-white px-1.5 py-0.5 rounded-full flex-shrink-0">
                          Текущая
                        </span>
                      )}
                    </div>
                    <p className="text-surface-500 text-xs">
                      {session.ipAddress ?? 'IP неизвестен'} · Вход {format(new Date(session.createdAt), 'd MMM yyyy, HH:mm', { locale: ru })}
                    </p>
                  </div>
                  {!session.isCurrent && (
                    <button
                      onClick={() => revokeMut.mutate(session.id)}
                      disabled={revokeMut.isPending}
                      title="Завершить сессию"
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-surface-500 hover:text-red-400 hover:bg-surface-700 transition-colors flex-shrink-0"
                    >
                      <LogOut size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* v5.0 — Notification preferences (per-type + quiet hours, backend Phase P3) */}
          <NotificationPrefsSection />

          {/* v5.0 — Privacy toggles (152-ФЗ ст. 5 + GDPR Art 7) */}
          <PrivacySection />

          {/* v5.0 — Account self-service (152-ФЗ ст. 14 + GDPR Art 17 + 20) */}
          <AccountSection onLoggedOut={() => navigate('/auth/login')} />

          {/* v5.5 — Language selector (i18n bootstrap; полный перевод постепенно) */}
          <LanguagePicker />

          {/* Danger zone */}
          <section className="bg-surface-900 border border-red-900/40 rounded-xl p-5">
            <h3 className="text-red-400 font-semibold mb-3">Выход</h3>
            <button
              onClick={async () => { await logout(); navigate('/auth/login'); }}
              className="flex items-center gap-2 py-2 px-4 rounded-lg bg-red-900/30 border border-red-800/50 text-red-300 hover:bg-red-900/50 transition-colors text-sm font-medium"
            >
              <LogOut size={15} />
              Выйти из аккаунта
            </button>
          </section>

        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Phase 24 — UI prefs: цвет @mention подсветки + клиентский список блокировок.
// ────────────────────────────────────────────────────────────────────────────
function MentionColorPicker() {
  const color = useUiPrefsStore((s) => s.mentionHighlightColor);
  const setColor = useUiPrefsStore((s) => s.setMentionHighlightColor);
  return (
    <section className="bg-surface-900 border border-surface-800 rounded-xl p-5">
      <h3 className="text-surface-100 font-semibold mb-2">Цвет подсветки @упоминаний</h3>
      <p className="text-xs text-surface-500 mb-3">
        Этим цветом подсвечивается ваш ник, когда вас упоминают в чате.
      </p>
      <div className="flex gap-2">
        {MENTION_COLOR_PRESETS.map((c) => (
          <button
            key={c}
            onClick={() => setColor(c)}
            className={`w-9 h-9 rounded-full border-2 transition-transform ${color === c ? 'border-white scale-110' : 'border-surface-700 hover:scale-105'}`}
            style={{ background: c }}
            title={c}
          />
        ))}
      </div>
    </section>
  );
}

function BlockedUsersList() {
  const ids = useBlockedUsersStore((s) => s.ids);
  const unblock = useBlockedUsersStore((s) => s.unblock);
  const clear = useBlockedUsersStore((s) => s.clear);
  if (ids.length === 0) return null;
  return (
    <section className="bg-surface-900 border border-surface-800 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-surface-100 font-semibold">Заблокированные пользователи</h3>
        <button onClick={() => clear()} className="text-xs text-red-400 hover:text-red-300">
          Очистить все
        </button>
      </div>
      <ul className="space-y-1">
        {ids.map((id) => (
          <li key={id} className="flex items-center justify-between bg-surface-800 rounded px-3 py-1.5">
            <span className="text-surface-300 text-sm font-mono truncate">{id}</span>
            <button
              onClick={() => unblock(id)}
              className="text-xs text-brand-400 hover:text-brand-300"
            >
              Разблокировать
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// v5.0 — Notification preferences (per-type opt-out + quiet hours).
// Backend Phase P3 (v4.2) had API; this is the first UI for it.
// ────────────────────────────────────────────────────────────────────────────

const NOTIF_TYPE_LABELS: Array<[
  NotificationTypeKey,
  /** label */ string,
  /** subtitle */ string,
]> = [
  ['DM',              'Личные сообщения',     'Кто-то пишет тебе в DM'],
  ['MENTION',         'Упоминания',           'Когда тебя @username в канале'],
  ['CHANNEL_INVITE',  'Приглашения в канал',  'Тебя добавили или ждут approve'],
  ['CALL_INCOMING',   'Входящие звонки',      'Принудительная плашка + рингтон'],
  ['CALL_MISSED',     'Пропущенные звонки',   'Сводка по пропущенным'],
  ['STORY_LIKE',      'Лайки на сторис',      'Кто-то поставил ❤️ на твою сторис'],
  ['STORY_COMMENT',   'Комменты на сторис',   'Новый коммент к твоей сторис'],
  ['STORY_REACTION',  'Реакции на сторис',    'Эмодзи-реакция на твою сторис'],
  ['WALL_LIKE',       'Лайки на стене',       'Кто-то лайкнул твой стикер'],
  ['WALL_COMMENT',    'Комменты на стене',    'Новый коммент на стикере'],
  ['FOLLOW',          'Новые подписчики',     'Кто-то начал следить за тобой'],
  ['FRIEND_REQUEST',  'Запросы в друзья',     'Кто-то предложил дружбу'],
  ['FRIEND_ACCEPTED', 'Принятые дружбы',      'Твой запрос принят'],
  ['KARMA_GAIN',      'Карма',                'Изменения твоей кармы'],
];

function NotificationPrefsSection() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['notif-prefs'],
    queryFn: () => notificationsApi.getPrefs(),
  });

  const patchMut = useMutation({
    mutationFn: (patch: Parameters<typeof notificationsApi.patchPrefs>[0]) =>
      notificationsApi.patchPrefs(patch),
    onSuccess: (next) => qc.setQueryData(['notif-prefs'], next),
  });

  const prefs = data?.prefs ?? {};
  const types = prefs.types ?? {};
  const quietHours = prefs.quietHours;
  const busy = isLoading || patchMut.isPending;

  const isTypeEnabled = (key: NotificationTypeKey) => types[key] !== false;

  const toggleType = (key: NotificationTypeKey, next: boolean) => {
    patchMut.mutate({ types: { [key]: next } });
  };

  const setQuietHours = (start: number, end: number) => {
    patchMut.mutate({ quietHours: { start, end, tz: quietHours?.tz ?? 'Europe/Moscow' } });
  };
  const disableQuietHours = () => patchMut.mutate({ quietHours: null });

  return (
    <section className="bg-surface-900 border border-surface-800 rounded-xl p-5">
      <h3 className="text-surface-100 font-semibold mb-1 flex items-center gap-2">
        <Bell size={16} className="text-brand-400" />
        Уведомления
      </h3>
      <p className="text-xs text-surface-500 mb-4">
        Что присылать в push, какие типы — мьютить. По типу = на push и на batch-flusher.
      </p>

      {/* Quiet hours */}
      <div className="bg-surface-800/40 rounded-lg p-3 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <BellOff size={14} className="text-surface-400" />
          <span className="text-sm text-surface-200 font-medium">Тихие часы</span>
        </div>
        <p className="text-xs text-surface-500 mb-3">
          В этом интервале push-уведомления не приходят. Внутри-приложения — приходят как обычно.
        </p>
        {quietHours ? (
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={quietHours.start}
              onChange={(e) => setQuietHours(Number(e.target.value), quietHours.end)}
              disabled={busy}
              className="bg-surface-800 border border-surface-700 rounded px-2 py-1 text-sm text-surface-100"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
              ))}
            </select>
            <span className="text-surface-400 text-sm">→</span>
            <select
              value={quietHours.end}
              onChange={(e) => setQuietHours(quietHours.start, Number(e.target.value))}
              disabled={busy}
              className="bg-surface-800 border border-surface-700 rounded px-2 py-1 text-sm text-surface-100"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
              ))}
            </select>
            <button
              onClick={disableQuietHours}
              disabled={busy}
              className="ml-2 text-xs text-surface-400 hover:text-red-300"
            >
              отключить
            </button>
            {quietHours.start > quietHours.end && (
              <span className="text-xs text-surface-500 w-full mt-1">через полночь</span>
            )}
          </div>
        ) : (
          <button
            onClick={() => setQuietHours(22, 8)}
            disabled={busy}
            className="text-xs text-brand-300 hover:text-brand-200"
          >
            Включить (по умолчанию 22:00 → 08:00)
          </button>
        )}
      </div>

      {/* Per-type toggles */}
      <div className="divide-y divide-surface-800">
        {NOTIF_TYPE_LABELS.map(([key, label, sub]) => (
          <PrivacyToggle
            key={key}
            title={label}
            subtitle={sub}
            checked={isTypeEnabled(key)}
            disabled={busy}
            onChange={(c) => toggleType(key, c)}
          />
        ))}
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// v5.0 — Privacy toggles (mirror of Android v5.0 PrivacySection).
// 152-ФЗ ст. 5 (data minimization) + GDPR Art 7 (subject control).
// ────────────────────────────────────────────────────────────────────────────
function PrivacySection() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['privacy'],
    queryFn: () => authApi.getPrivacy(),
  });

  const patchMut = useMutation({
    mutationFn: (patch: Partial<PrivacySettings>) => authApi.patchPrivacy(patch),
    onSuccess: (next) => qc.setQueryData(['privacy'], next),
  });

  const v: PrivacySettings = data ?? {
    hideLastSeen: false,
    hideOnline: false,
    hideReadReceipts: false,
    whoCanCallMe: 'EVERYONE',
    whoCanAddMeToChannels: 'EVERYONE',
  };
  const busy = isLoading || patchMut.isPending;

  return (
    <section className="bg-surface-900 border border-surface-800 rounded-xl p-5">
      <h3 className="text-surface-100 font-semibold mb-1 flex items-center gap-2">
        <EyeOff size={16} className="text-brand-400" />
        Конфиденциальность
      </h3>
      <p className="text-xs text-surface-500 mb-4">
        Управляй тем, что видят о тебе другие. Изменения применяются мгновенно.
      </p>

      <PrivacyToggle
        title="Скрыть «был онлайн»"
        subtitle="Другие не увидят дату последнего входа"
        checked={v.hideLastSeen}
        disabled={busy}
        onChange={(checked) => patchMut.mutate({ hideLastSeen: checked })}
      />
      <div className="border-t border-surface-800 my-2" />
      <PrivacyToggle
        title="Скрыть онлайн-статус"
        subtitle="Все увидят тебя как «не в сети», даже если ты в SWIPA"
        checked={v.hideOnline}
        disabled={busy}
        onChange={(checked) => patchMut.mutate({ hideOnline: checked })}
      />
      <div className="border-t border-surface-800 my-2" />
      <PrivacyToggle
        title="Скрыть «прочитано»"
        subtitle="Двойная галочка не появится у собеседников"
        checked={v.hideReadReceipts}
        disabled={busy}
        onChange={(checked) => patchMut.mutate({ hideReadReceipts: checked })}
      />
      <div className="border-t border-surface-800 my-3" />
      <PrivacyAudienceSelect
        title="Кто может мне звонить"
        subtitle="«Только друзья» — взаимные accepted-друзья. «Никто» — все звонки отклоняются."
        value={v.whoCanCallMe}
        disabled={busy}
        onChange={(next) => patchMut.mutate({ whoCanCallMe: next })}
      />
      <div className="border-t border-surface-800 my-3" />
      <PrivacyAudienceSelect
        title="Кто может добавлять меня в каналы"
        subtitle="Действует на инвайт-ссылки. Сам по join() в публичный канал — всегда можно."
        value={v.whoCanAddMeToChannels}
        disabled={busy}
        onChange={(next) => patchMut.mutate({ whoCanAddMeToChannels: next })}
      />
    </section>
  );
}

// v5.1 — dropdown row for PrivacyAudience.
// Стилизован под dark surface-* palette остального settings.
function PrivacyAudienceSelect({
  title, subtitle, value, disabled, onChange,
}: {
  title: string;
  subtitle: string;
  value: PrivacyAudience;
  disabled?: boolean;
  onChange: (next: PrivacyAudience) => void;
}) {
  return (
    <div className="flex items-start gap-3 py-2">
      <div className="flex-1 min-w-0">
        <div className="text-sm text-surface-100 font-medium">{title}</div>
        <div className="text-xs text-surface-400">{subtitle}</div>
      </div>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value as PrivacyAudience)}
        className={`bg-surface-800 border border-surface-700 text-surface-100 text-sm rounded-md px-2 py-1
          ${disabled ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
      >
        {(['EVERYONE', 'FRIENDS', 'NOBODY'] as PrivacyAudience[]).map((opt) => (
          <option key={opt} value={opt}>
            {PRIVACY_AUDIENCE_LABELS[opt]}
          </option>
        ))}
      </select>
    </div>
  );
}

function PrivacyToggle({
  title, subtitle, checked, disabled, onChange,
}: {
  title: string;
  subtitle: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="flex-1 min-w-0">
        <div className="text-sm text-surface-100 font-medium">{title}</div>
        <div className="text-xs text-surface-400">{subtitle}</div>
      </div>
      <button
        type="button"
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        aria-checked={checked}
        role="switch"
        className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0
          ${checked ? 'bg-brand-600' : 'bg-surface-700'}
          ${disabled ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
      >
        <span
          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all
            ${checked ? 'left-5' : 'left-0.5'}`}
        />
      </button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// v5.0 — Account self-service: data export + soft-delete.
// 152-ФЗ ст. 14 ч. 1 / GDPR Art 17 (erasure) + Art 20 (portability).
// ────────────────────────────────────────────────────────────────────────────
function AccountSection({ onLoggedOut }: { onLoggedOut: () => void }) {
  const { logout } = useAuthStore();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pwd, setPwd] = useState('');
  const [reason, setReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await authApi.exportAccount();
    } catch (e: any) {
      setExportError(e?.message ?? 'Не удалось скачать');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (!pwd) {
      setDeleteError('Введите пароль для подтверждения');
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      await authApi.deleteAccount(pwd, reason || undefined);
      // Server already revoked all refresh tokens. Force the local store to clear.
      await logout();
      onLoggedOut();
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? e?.message ?? 'Ошибка удаления';
      const hint = msg.toLowerCase().includes('credentials') || e?.response?.status === 401
        ? 'Неверный пароль'
        : msg;
      setDeleteError(hint);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="bg-surface-900 border border-surface-800 rounded-xl p-5">
      <h3 className="text-surface-100 font-semibold mb-1 flex items-center gap-2">
        <Shield size={16} className="text-brand-400" />
        Аккаунт и данные
      </h3>
      <p className="text-xs text-surface-500 mb-4">
        Согласно 152-ФЗ и GDPR ты можешь скачать копию своих данных или удалить аккаунт.
      </p>

      {/* Export */}
      <button
        onClick={handleExport}
        disabled={exporting}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-surface-800 hover:bg-surface-700 disabled:opacity-50 transition-colors text-left"
      >
        <Download size={16} className="text-brand-300 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-surface-100 font-medium">
            {exporting ? 'Готовим экспорт…' : 'Скачать мои данные'}
          </div>
          <div className="text-xs text-surface-400">
            JSON со всем: профиль, сообщения, медиа-метаданные, friends, stories
          </div>
        </div>
      </button>
      {exportError && (
        <p className="mt-2 text-xs text-red-400">{exportError}</p>
      )}

      {/* Delete */}
      <button
        onClick={() => { setConfirmOpen(true); setDeleteError(null); }}
        className="mt-3 w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-red-900/30 border border-red-900/50 hover:bg-red-900/50 transition-colors text-left"
      >
        <Trash2 size={16} className="text-red-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-red-300 font-medium">Удалить аккаунт</div>
          <div className="text-xs text-surface-400">
            Soft-delete + 30 дней на восстановление через поддержку
          </div>
        </div>
      </button>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => !deleting && setConfirmOpen(false)}>
          <div
            className="bg-surface-900 border border-red-900/50 rounded-xl p-5 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-3 text-red-400">
              <AlertTriangle size={18} />
              <h4 className="font-semibold">Удалить аккаунт?</h4>
            </div>
            <p className="text-sm text-surface-300 mb-4">
              Все твои данные (сообщения, истории, друзья, медиа) останутся ещё <strong>30 дней</strong>,
              потом сотрутся автоматически. Восстановление возможно только через поддержку.
              <br /><br />
              Введи пароль для подтверждения.
            </p>
            <input
              type="password"
              autoFocus
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="Пароль"
              className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-100 placeholder:text-surface-500 mb-2 focus:outline-none focus:border-red-500"
              disabled={deleting}
            />
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Причина (опционально, для нашей статистики)"
              className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-100 placeholder:text-surface-500 mb-3 focus:outline-none focus:border-surface-600"
              disabled={deleting}
              maxLength={500}
            />
            {deleteError && (
              <p className="mb-3 text-xs text-red-400">{deleteError}</p>
            )}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={deleting}
                className="px-4 py-2 rounded-lg bg-surface-800 hover:bg-surface-700 text-sm text-surface-200 disabled:opacity-50"
              >
                Отмена
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting || !pwd}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-sm text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? 'Удаляем…' : 'Удалить навсегда'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
